import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Shield } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Props {
  factorId: string;
  open: boolean;
  onSuccess: () => void;
  /** If omitted, the dialog cannot be dismissed — used for hard gates. */
  onCancel?: () => void;
  title?: string;
  description?: string;
}

export default function MfaChallengeDialog({ factorId, open, onSuccess, onCancel, title, description }: Props) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setSubmitting(true);
    try {
      const { data: challenge, error: cErr } = await supabase.auth.mfa.challenge({ factorId });
      if (cErr || !challenge) throw new Error(cErr?.message ?? 'Could not start challenge');

      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (vErr) throw new Error(vErr.message);

      setCode('');
      onSuccess();
    } catch (err: unknown) {
      toast({
        title: 'Incorrect code',
        description: err instanceof Error ? err.message : 'Verification failed. Please try again.',
        variant: 'destructive',
      });
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  // When there is no cancel handler the dialog cannot be closed by the user.
  const handleOpenChange = (open: boolean) => {
    if (!open && onCancel) onCancel();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-sm"
        onInteractOutside={!onCancel ? e => e.preventDefault() : undefined}
        onEscapeKeyDown={!onCancel ? e => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <DialogTitle>{title ?? 'Two-factor authentication'}</DialogTitle>
          </div>
          <DialogDescription>
            {description ?? 'Enter the 6-digit code from your authenticator app.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="mfa-challenge-code">Verification code</Label>
            <Input
              id="mfa-challenge-code"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }}
            />
          </div>

          <p className="text-xs text-muted-foreground">
            Lost access to your authenticator?{' '}
            <a href="/contact" className="text-primary hover:underline">Contact support</a> for recovery.
          </p>

          <div className="flex gap-2 justify-end pt-1">
            {onCancel && (
              <Button variant="outline" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
            )}
            <Button onClick={handleVerify} disabled={code.length !== 6 || submitting}>
              {submitting
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Verifying…</>
                : 'Verify'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
